// B4 triage modals for a reported issue: acknowledge / start investigation
// (severity select, FIND-12 — hydrated from the current triage severity or
// the reporter's suggestion and always sent), resolve (reason required),
// and escalate (opens a DefaultCase with the pinned "Reported issue"
// trigger; the description prefills from the report).

import { useState } from "react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  COMPLIANCE_REPORT_SEVERITIES,
  type ComplianceIntakeReportDto,
  type ComplianceIntakeReportSeverity,
} from "@/api/compliance/intakeReportTypes";
import {
  useAcknowledgeIntakeReport,
  useEscalateIntakeReport,
  useInvestigateIntakeReport,
  useResolveIntakeReport,
} from "@/hooks/compliance/useIntakeReports";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface TriageModalProps {
  report: ComplianceIntakeReportDto;
  onClose: () => void;
}

function SeveritySelect({
  value,
  onChange,
  testId,
}: {
  value: "" | ComplianceIntakeReportSeverity;
  onChange: (v: "" | ComplianceIntakeReportSeverity) => void;
  testId: string;
}) {
  return (
    <select
      className="border border-border-light rounded-md h-10 px-space-2 text-small bg-white-pure"
      value={value}
      onChange={(e) =>
        onChange(e.target.value as "" | ComplianceIntakeReportSeverity)
      }
      data-testid={testId}
    >
      <option value="">Unrated</option>
      {COMPLIANCE_REPORT_SEVERITIES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

/** Shared acknowledge / investigate form — only the verb differs. */
export function TriageStepModal({
  report,
  mode,
  onClose,
}: TriageModalProps & { mode: "acknowledge" | "investigate" }) {
  const toast = useToast();
  const acknowledge = useAcknowledgeIntakeReport(report.loanId, report.id);
  const investigate = useInvestigateIntakeReport(report.loanId, report.id);
  const mutation = mode === "acknowledge" ? acknowledge : investigate;

  // FIND-12 — hydrate from the current triage severity (or the reporter's
  // suggestion) and always send the selected value.
  const [severity, setSeverity] = useState<"" | ComplianceIntakeReportSeverity>(
    report.triageSeverity ?? report.suggestedSeverity ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    mutation.mutate(
      { triageSeverity: severity || null },
      {
        onSuccess: () => {
          toast.success(
            mode === "acknowledge"
              ? "Report acknowledged."
              : "Investigation started.",
          );
          onClose();
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={mode === "acknowledge" ? "Acknowledge report" : "Start investigation"}
      description={report.category}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={mutation.isPending}
            data-testid="report-triage-submit"
          >
            {mode === "acknowledge" ? "Acknowledge" : "Start investigation"}
          </Button>
        </>
      }
    >
      <FormField label="Severity">
        <SeveritySelect
          value={severity}
          onChange={setSeverity}
          testId="report-triage-severity"
        />
      </FormField>
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

export function ResolveReportModal({ report, onClose }: TriageModalProps) {
  const toast = useToast();
  const resolve = useResolveIntakeReport(report.loanId, report.id);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (!reason.trim()) {
      setError("A resolution reason is required.");
      return;
    }
    resolve.mutate(
      { reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success("Report resolved.");
          onClose();
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Resolve report"
      description={report.category}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={resolve.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={resolve.isPending}
            data-testid="report-resolve-submit"
          >
            Resolve
          </Button>
        </>
      }
    >
      <FormField label="Resolution reason" required>
        <textarea
          className="min-h-[80px] px-space-2 py-space-1 border border-border-light rounded-md text-small w-full"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this report needs no further action."
          data-testid="report-resolve-reason"
        />
      </FormField>
      {error && (
        <p
          className="text-caption text-status-error mt-space-2"
          role="alert"
          data-testid="report-resolve-error"
        >
          {error}
        </p>
      )}
    </Modal>
  );
}

export function EscalateReportModal({ report, onClose }: TriageModalProps) {
  const toast = useToast();
  const escalate = useEscalateIntakeReport(report.loanId, report.id);
  const tz = useUserTimeZone();
  const [triggerDate, setTriggerDate] = useState(
    isoDateInTz(new Date(), tz),
  );
  const [description, setDescription] = useState(report.description);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (!triggerDate) {
      setError("A trigger date is required.");
      return;
    }
    if (description.trim().length < 10) {
      setError("Describe the trigger in at least 10 characters.");
      return;
    }
    escalate.mutate(
      // The case-open precedent: serialize the date input as a full ISO
      // instant for the DateTimeOffset binder.
      { triggerDate: new Date(triggerDate).toISOString(), description: description.trim() },
      {
        onSuccess: () => {
          toast.success("Default case opened from this report.");
          onClose();
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Escalate to default case"
      description="Opens a DefaultCase carrying this report as its documented trigger."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={escalate.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={escalate.isPending}
            data-testid="report-escalate-submit"
          >
            {escalate.isPending ? "Opening case…" : "Open default case"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Trigger">
          {/* Pinned — the lineage key. */}
          <Input value="Reported issue" disabled data-testid="report-escalate-trigger" />
        </FormField>
        <FormField label="Trigger date" required>
          <Input
            type="date"
            value={triggerDate}
            onChange={(e) => setTriggerDate(e.target.value)}
            data-testid="report-escalate-date"
          />
        </FormField>
        <FormField label="Description" required>
          <textarea
            className="min-h-[80px] px-space-2 py-space-1 border border-border-light rounded-md text-small w-full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="report-escalate-description"
          />
        </FormField>
        {error && (
          <p
            className="text-caption text-status-error m-0"
            role="alert"
            data-testid="report-escalate-error"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
